import React, { MouseEvent, RefObject, useCallback, useEffect, useMemo, useState } from 'react';

import { CustomOfferFormValues } from '@/typings/formValues';
import { yupResolver } from '@hookform/resolvers/yup';
import { allCountries } from 'country-telephone-data';
import { useSnackbar } from 'notistack';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { v4 as uuid_v4 } from 'uuid';
import { isReasonOtherTextRequired } from '~/validation/schemas/customOfferValidationPredicates';
import { customOfferGHStoreSchema, editableCustomOfferSchema } from '~/validation/schemas/customOffers';

import CheckIcon from '@mui/icons-material/Check';
import HelpIcon from '@mui/icons-material/Help';
import SaveIcon from '@mui/icons-material/Save';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';

import { getCurrencyCodes, getRegionByCurrency } from '@luxuryescapes/lib-regions';

import DateWidget from '~/components/Common/Elements/DateWidget';
import OrderDetailNotes from '~/components/Purchases/OrderDetail/OrderDetailNotes';

import { OPERATIONS_SUPPORT } from '~/consts/notes';
import stores from '~/consts/stores';
import { UTM_MEDIUM } from '~/consts/utm';

import useAppNavigation from '~/hooks/useAppNavigation';
import useCurrentUser from '~/hooks/useCurrentUser';
import useToggleState from '~/hooks/useToggleState';

import OffersService from '~/services/OffersService';
import * as OrdersService from '~/services/OrdersService';
import { addDays, formatDateISO, isBefore, newDate } from '~/services/TimeService';

import { CurrencyDict, fetchSpotAndContractedRates } from '~/utils/customOfferFormUtils';

import { RichTextEditor } from '../Forms/fields';

import RatesTable from './CustomOfferForm/RatesTable';
import CustomOfferInternalDiscountField from './CustomOfferInternalDiscountField';
import CustomOfferLoyaltyCalculator from './CustomOfferLoyaltyCalculator';
import {
  CustomOfferLinkedPackageOfferField,
  CustomOfferPackageComponentsSection,
} from './CustomOfferPackageAssociations';
import { CustomOfferRequiredFieldsLegend } from './CustomOfferRequiredFieldsLegend';
import { TravellersSection, TravellersSectionRef } from './CustomOfferTravellersSection';
import { HtmlTooltip } from './HTMLTooltip';
import { WholesaleAgentAutocomplete } from './WholesaleAgentAutocomplete';
import {
  LE_ENTITY_AUS,
  OFFER_TYPES,
  REASONS,
  SALES_ATTRIBUTION_MARGIN_MAX_VALUE,
  WHOLESALE_AGENT_LIST,
  getSalesAttributionList,
} from './constants';
import { customOfferMerchantFeeFieldLabel } from './customOfferMerchantFeeFieldLabel';
import { getOfferTypeSelectOptions, isOfferTypeSelectLocked } from './customOfferPackagePermissions';
import { ConfirmDeleteDialog } from './dialogs/ConfirmDeleteDialog';
import { buildPendingPointsPayload, fetchLoyaltyTier } from './loyaltyUtils';
import { getIsChangedFromLeLiveOffer, isFreeTextReason } from './utils';

const phonePrefixOptions: Array<{ value: string; label: string }> = allCountries.map(({ dialCode, name }) => ({
  value: dialCode,
  label: `+${dialCode} ${name}`,
}));

type Props = {
  user: App.User;
  currentOffer?: App.CustomOffer;
  hasOrders?: boolean;
  order?: App.Order;
  onUpdate?: (offer: App.CustomOffer) => Promise<void>;
  paymentDate?: string;
  changeFormType: (
    type: App.CustomOfferType,
    internalDiscountTracking?: string | null,
    nextVendorName?: string,
  ) => void;
  offerTypes: typeof OFFER_TYPES;
  brand: App.Brands;
  travellersSectionRef: RefObject<TravellersSectionRef>;
  isBusinessTraveller: boolean;
  customerHasPaid: boolean;
};

const buildDefaultValues = (offer?: App.CustomOffer): CustomOfferFormValues => {
  const today = new Date().toISOString().split('T')[0];
  const item = offer?.items[0];
  const travellers = offer?.items[0]?.travellers;

  let reasonDefaultValue: string;
  let reasonOtherDefaultValue: string;

  if (offer?.reason && isFreeTextReason(offer.reason)) {
    reasonDefaultValue = 'other';
    reasonOtherDefaultValue = offer.reason;
  } else {
    reasonDefaultValue = offer?.reason ?? '';
    reasonOtherDefaultValue = '';
  }

  return {
    item: {
      type: 'ics_store',
      name: item?.name ?? '',
      description: item?.description ?? '',
      vendor: item?.vendor ?? '',
      vendor_reference_number: item?.vendor_reference_number ?? '',
      salesforce_case_number: item?.salesforce_case_number ?? '',
      service_information: item?.service_information ?? '',
      start_date: item?.start_date ?? today,
      end_date: item?.end_date ?? formatDateISO(addDays(1)),
    },
    travellers:
      travellers?.length > 0
        ? travellers
        : [
            {
              name: '',
              type: 'adult',
              country: '',
              date_of_birth: null,
            },
          ],
    price: offer?.price ? Number(offer.price) / 100 : 0,
    price_currency: offer?.price_currency ?? 'AUD',
    cost_price: offer?.cost_price ? Number(offer.cost_price) / 100 : 0,
    cost_price_currency: offer?.cost_price_currency ?? 'AUD',
    due_date: offer?.due_date ?? formatDateISO(addDays(1)),
    sold_at: today,
    payment_terms: 'full_payment',
    images: offer?.images ?? [],
    payment_metadata: {
      margin: offer?.payment_metadata?.margin ? offer.payment_metadata.margin / 100 : 0,
      le_service_fee: 0,
      service_fee_type: 'No fee',
      payment_type: 'full_payment',
      customer_exchange_rate: 1,
      customer_deposit_1_amount: 0,
      customer_deposit_1_due_date: null,
      deposit_pi_number_customer: null,
      final_amount_customer: offer?.price ? Number(offer.price) / 100 : 0,
      block_customer_payment_notifications: false,
      merchant_fee_enabled: offer?.payment_metadata?.merchant_fee_enabled ?? true,
      vendor_payment_type: 'full_payment',
      vendor_exchange_rate: offer?.payment_metadata?.vendor_exchange_rate ?? 1,
      vendor_due_date: null,
      vendor_was_paid: false,
      vendor_deposit_1_amount: null,
      vendor_deposit_1_due_date: null,
      final_payment_vcc_digits: null,
      deposit_vcc_digits: null,
      final_amount_vendor: offer?.cost_price ? Number(offer.cost_price) / 100 : 0,
      vendor_payment_date: null,
      vendor_deposit_payment_date: null,
      le_entity: offer?.payment_metadata?.le_entity ?? LE_ENTITY_AUS,
      sales_attribution: offer?.payment_metadata?.sales_attribution ?? UTM_MEDIUM,
      created_by: offer?.payment_metadata?.created_by ?? '',
      sales_agent_name: offer?.payment_metadata?.sales_agent_name ?? '',
      internal_discount_tracking: offer?.payment_metadata?.internal_discount_tracking ?? '',
    },
    notes: offer?.notes ?? '',
    created_from_item_id: offer?.created_from_item_id ?? null,
    fk_package_custom_offer_id: offer?.fk_package_custom_offer_id ?? null,
    customer_phone_prefix: offer?.customer_phone_prefix ?? '',
    customer_phone_number: offer?.customer_phone_number ?? '',
    reason: reasonDefaultValue,
    reasonOther: reasonOtherDefaultValue,
    hiddenFromCustomer: offer?.hidden_from_customer ?? false,
    supplier: 'Other',
    pdf_filename: offer?.pdf_filename ?? null,
    pdf_s3_key: offer?.pdf_s3_key ?? null,
    pdf_download_url: offer?.pdf_download_url ?? null,
  };
};

type Payload = Omit<App.CustomOffer, 'id' | 'status' | 'has_vcc' | 'created_at' | 'updated_at'> & {
  pdf?: {
    filename: string;
    content: string;
    mimeType: string;
  };
};

const buildPayload = (user: App.User, formData: CustomOfferFormValues): Payload => {
  const { item, travellers, payment_metadata, reason, reasonOther, pdf, hiddenFromCustomer, ...rest } = formData;

  const payload: Payload = {
    ...rest,
    hidden_from_customer: hiddenFromCustomer,
    price: Math.round(rest.price * 100),
    cost_price: Math.round(rest.cost_price * 100),
    due_date: formatDateISO(rest.due_date),
    sold_at: formatDateISO(rest.sold_at),
    payment_metadata: {
      ...payment_metadata,
      vendor_due_date: null,
      final_amount_customer: Math.round(rest.price * 100),
      final_amount_vendor: Math.round(payment_metadata.final_amount_vendor * 100),
      le_service_fee: 0,
      margin: Math.round(payment_metadata.margin * 100),
      le_entity: payment_metadata?.le_entity,
      additional_vendor_payment: payment_metadata.additional_vendor_payment
        ? Math.round(payment_metadata.additional_vendor_payment * 100)
        : 0,
      additional_margin:
        payment_metadata.additional_margin != null ? Math.round(payment_metadata.additional_margin * 100) : null,
      internal_discount_tracking: payment_metadata.internal_discount_tracking?.trim()
        ? payment_metadata.internal_discount_tracking.trim()
        : null,
      reason_additional_info: payment_metadata?.reason_additional_info,
      reason_issue_raised: payment_metadata?.reason_issue_raised,
    },
    name: item.name,
    fk_customer_id: user.id_member,
    customer_full_name: user.fullName,
    images: [],
    items: [
      {
        ...item,
        start_date: formatDateISO(item.start_date),
        end_date: formatDateISO(item.end_date),
        travellers: travellers.map((traveller) => ({
          ...traveller,
          name: traveller.name?.trim() === '' ? null : traveller.name,
          date_of_birth: traveller.date_of_birth ? formatDateISO(traveller.date_of_birth) : null,
        })),
      },
    ],
    ...(pdf?.content && { pdf: { filename: pdf.filename, content: pdf.content, mimeType: pdf.mimeType } }),
  };

  if (reason && reason === 'other' && reasonOther) {
    payload.reason = reasonOther;
  } else {
    payload.reason = reason ?? undefined;
  }

  return payload;
};

const buildPaymentPayload = async (user: App.User, offer: App.CustomOffer, brand: App.Brands = 'luxuryescapes') => {
  const transactionKey = uuid_v4();
  const total = offer.payment_metadata.final_amount_customer / 100;
  const regionCode = getRegionByCurrency(offer.price_currency, brand);

  const pendingPoints = await buildPendingPointsPayload(
    user,
    offer,
    transactionKey,
    offer.payment_metadata?.sales_attribution,
    brand,
  );
  const tier = pendingPoints ? await fetchLoyaltyTier(user.id_member) : null;

  return {
    brand,
    currency_code: offer.price_currency,
    region_code: regionCode.code,
    customer_id: user.id_member,
    customer_email: user.email,
    customer_phone_prefix: offer.customer_phone_prefix,
    customer_phone: offer.customer_phone_number,
    utm_source: brand,
    utm_medium: offer?.payment_metadata?.sales_attribution ?? UTM_MEDIUM,
    transaction_key: transactionKey,
    type: 'custom_offer',
    items: offer.items.map((item) => ({
      ...item,
      transaction_key: transactionKey,
      type: 'custom_offer',
      custom_offer_id: offer.id,
      due_date: offer.due_date,
      total,
      custom_item_type: item.type,
    })),
    ...(pendingPoints && tier && { lux_loyalty: { pendingPoints, tier } }),
  };
};

export default function CustomOfferForm({
  user,
  hasOrders,
  order,
  onUpdate,
  currentOffer,
  changeFormType,
  offerTypes,
  brand,
  travellersSectionRef,
  isBusinessTraveller,
  customerHasPaid,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const { user: currentAgent } = useCurrentUser();

  const [offer, setOffer] = useState<App.CustomOffer | undefined>(currentOffer);
  const [inclusionsDiscussed, setInclusionsDiscussed] = useState(offer?.status === 'approved');
  const [termsExplained, setTermsExplained] = useState(offer?.status === 'approved');
  const [sendStatus, setSendStatus] = useState<Utils.ProcessingState>('idle');
  const [contractFxRates, setContractFxRates] = useState<CurrencyDict | undefined>(undefined);
  const [customerFxRates, setCustomerFxRates] = useState<CurrencyDict | undefined>(undefined);

  const [pricingState, setPricingState] = useState({
    margin: 0,
    cost_price: 0,
    sell_price: 0,
    vendor_exchange_rate: 1,
  });

  const { isToggled: deleteDialogOpen, toggleOn: openDeleteDialog, toggleOff: closeDeleteDialog } = useToggleState();

  const { push, replace } = useAppNavigation();

  const isEditMode = !!offer?.id;

  // Commercial want ICS Custom offers vendor currency limited to the below three currencies
  const customerCurrencies = useMemo(
    () =>
      getCurrencyCodes(brand)
        .filter((currency) => ['AUD', 'IDR', 'USD'].includes(currency))
        .sort((a, b) => a.localeCompare(b)),
    [brand],
  );

  const costPriceCurrencies = useMemo(() => {
    if (customerFxRates) {
      return Object.keys(customerFxRates);
    }

    return [];
  }, [customerFxRates]);

  useEffect(() => {
    async function fetchFxRates() {
      const { customerFxRates, contractFxRates } = await fetchSpotAndContractedRates(
        customerCurrencies,
        enqueueSnackbar,
      );
      setCustomerFxRates(customerFxRates);
      setContractFxRates(contractFxRates);
    }
    fetchFxRates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    register,
    handleSubmit,
    control,
    formState: { isSubmitting, errors },
    watch,
    clearErrors,
    setValue,
  } = useForm<CustomOfferFormValues>({
    defaultValues: buildDefaultValues(offer),
    context: { editMode: isEditMode },
    resolver: yupResolver((hasOrders ? editableCustomOfferSchema : customOfferGHStoreSchema) as any),
    reValidateMode: 'onChange',
  });

  const salesAttribution = watch('payment_metadata.sales_attribution');
  const reason = watch('reason');
  const itemType = watch('item.type');
  const itemVendor = watch('item.vendor');
  const supplier = watch('supplier');
  const costPriceCurrencyCode = watch('cost_price_currency');
  const margin = watch('payment_metadata.margin');
  const costPrice = watch('cost_price');
  const vendorExchangeRate = watch('payment_metadata.vendor_exchange_rate');
  const priceCurrencyCode = watch('price_currency');

  const isCustomOfferPackage = itemType === 'custom-offer-package';
  const offerTypeSelectOptions = useMemo(
    () => getOfferTypeSelectOptions(offerTypes, currentAgent, itemType),
    [offerTypes, currentAgent, itemType],
  );
  const isOfferTypeSelectDisabled = isSubmitting || isOfferTypeSelectLocked(currentAgent, itemType);
  const isActionsDisabled = isSubmitting || sendStatus == 'processing';
  const isSendDisabled = !inclusionsDiscussed || !termsExplained || isActionsDisabled;
  const isAdditionalMarginVisible = salesAttribution === SALES_ATTRIBUTION_MARGIN_MAX_VALUE;
  const shouldLockLeLiveAttribution =
    isEditMode && customerHasPaid && offer?.payment_metadata?.sales_attribution === stores.le_live.utmMedium;

  const showSendButton = !!offer?.id && !hasOrders;

  const isChangedFromLeLiveOffer = getIsChangedFromLeLiveOffer({
    offer,
    nextItemType: itemType,
  });

  /** LE LIVE has no real vendor split; once switched away during edit, allow vendor cost/margin edits with an order. */
  const lockVendorPriceFieldsForPaidOrder = hasOrders && !isChangedFromLeLiveOffer;

  useEffect(() => {
    if (isCustomOfferPackage) {
      setValue('fk_package_custom_offer_id', null);
    }
  }, [isCustomOfferPackage, setValue]);

  const onSubmit: SubmitHandler<CustomOfferFormValues> = useCallback(
    async (formData) => {
      try {
        const payload = buildPayload(user, formData);
        payload.is_changed_from_le_live_offer = isChangedFromLeLiveOffer;
        if (shouldLockLeLiveAttribution) {
          payload.payment_metadata.sales_attribution = stores.le_live.utmMedium;
        }

        const response = offer?.id
          ? await OffersService.updateCustomOffer(offer.id, payload)
          : await OffersService.createCustomOffer(payload);

        const updatedOffer = response.result as App.CustomOffer;

        setOffer(updatedOffer);

        if (onUpdate) {
          await onUpdate(updatedOffer);
        }

        enqueueSnackbar(
          offer?.id ? 'Custom quote has been successfully updated' : 'Custom quote has been successfully created',
          { variant: 'success' },
        );

        replace(`/users/${user.id_member}/custom-offers/${updatedOffer.id}`);
      } catch (err) {
        enqueueSnackbar(`Can't process custom quote: ${err.message}`, { variant: 'error' });
      }
    },
    [user, isChangedFromLeLiveOffer, shouldLockLeLiveAttribution, offer, onUpdate, enqueueSnackbar, replace],
  );

  const handleCostPriceCurrencyChange = useCallback(
    (name: string, value: string) => {
      if (contractFxRates) {
        const fieldName = name as keyof CustomOfferFormValues;
        const costPriceCurrencyCode = value;

        const selectCostPriceFxRate = contractFxRates[costPriceCurrencyCode];

        if (!selectCostPriceFxRate || selectCostPriceFxRate === 0) {
          enqueueSnackbar(`Exchange rate not found for ${costPriceCurrencyCode}`, { variant: 'error' });
        }

        setValue('payment_metadata.vendor_exchange_rate', selectCostPriceFxRate ?? 0);

        if (costPriceCurrencyCode === priceCurrencyCode) {
          setValue('payment_metadata.customer_exchange_rate', selectCostPriceFxRate ?? 0);
        }
        setValue(fieldName, costPriceCurrencyCode);
      }
    },
    [priceCurrencyCode, contractFxRates, setValue, enqueueSnackbar],
  );

  const onSendPayment = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    if (!offer?.id) {
      return;
    }

    try {
      setSendStatus('processing');

      const dueDate = offer.due_date;
      if (isBefore(dueDate)) {
        throw new Error('Due date must be in the future');
      }

      const offerResponse = await OffersService.setCustomOfferStatus(offer.id, 'approved');
      const updatedOffer = offerResponse.result as App.CustomOffer;

      setOffer(updatedOffer);

      const payload = await buildPaymentPayload(user, updatedOffer, brand);

      const response = await OrdersService.createOrder(payload);

      if (offer.notes && offer.notes.length > 0) {
        await OrdersService.addOrderNote(response.result.id_orders, {
          comment: offer.notes,
          comment_type: OPERATIONS_SUPPORT,
        });
      }

      enqueueSnackbar('Payment request has been successfully created', { variant: 'success' });
      setSendStatus('success');

      setTimeout(() => window.location.reload(), 200);
    } catch (err) {
      setSendStatus('failed');
      enqueueSnackbar(`Can't create payment request: ${err.message}`, { variant: 'error' });
    }
  };

  const deleteOffer = async () => {
    OffersService.deleteCustomOffer(offer.id).then(() => push(`/users/${user.id_member}`));
  };

  const calculateSellPrice = useCallback((costPrice: number, margin: number, vendorExchangeRate: number) => {
    return (Number(costPrice) + Number(margin)) / Number(vendorExchangeRate);
  }, []);

  const updatePricingState = useCallback(
    (updates: Partial<typeof pricingState>) => {
      setPricingState((prev) => {
        const newState = { ...prev, ...updates };

        if (updates.margin !== undefined || updates.cost_price !== undefined) {
          const costPrice = updates.cost_price !== undefined ? updates.cost_price : prev.cost_price;
          const margin = updates.margin !== undefined ? updates.margin : prev.margin;
          const vendorExchangeRate =
            updates.vendor_exchange_rate !== undefined ? updates.vendor_exchange_rate : prev.margin;
          newState.sell_price = calculateSellPrice(costPrice, margin, vendorExchangeRate);
        }

        return newState;
      });
    },
    [calculateSellPrice],
  );

  useEffect(() => {
    const currentMargin = margin || 0;
    const currentCostPrice = costPrice || 0;
    const currentVendorExchangeRate = vendorExchangeRate || 1;

    updatePricingState({
      margin: currentMargin,
      cost_price: currentCostPrice,
      vendor_exchange_rate: currentVendorExchangeRate,
    });
  }, [margin, costPrice, updatePricingState, vendorExchangeRate]);

  useEffect(() => {
    if (!hasOrders) {
      setValue('price', pricingState.sell_price);
      setValue('payment_metadata.final_amount_customer', pricingState.sell_price);
      clearErrors(['price', 'payment_metadata.final_amount_customer']);
    }
  }, [hasOrders, pricingState.sell_price, setValue, clearErrors]);

  const handleOfferTypeChange = useCallback(
    (event: SelectChangeEvent<App.CustomOfferType>) => {
      const nextType = event.target.value as App.CustomOfferType;
      const currentInternalDiscountTracking = watch('payment_metadata.internal_discount_tracking');
      const currentVendorName = watch('item.vendor')?.trim() || '';
      setValue('supplier', 'Other');
      setValue('item.type', nextType);
      changeFormType(nextType, currentInternalDiscountTracking, currentVendorName);
    },
    [setValue, watch, changeFormType],
  );

  const handleSupplierChange = useCallback(
    (_, value: string) => {
      setValue('supplier', value);
      setValue('item.vendor', value === 'Other' ? '' : value);
    },
    [setValue],
  );

  const offerTypeSuppliers = useMemo(() => {
    return offerTypes.find((offerType) => offerType.value === itemType)?.suppliers || ['Other'];
  }, [offerTypes, itemType]);

  return (
    <form noValidate onSubmit={handleSubmit(onSubmit)} data-testid="custom-offer-form">
      <Stack direction="row" justifyContent="space-between">
        <Stack direction="row" spacing={4}>
          <Typography variant="h6" fontWeight="normal" color="GrayText">
            Customer: {user.fullName}
          </Typography>
          <Typography variant="h6" fontWeight="normal" color="GrayText">
            Email: {user.email}
          </Typography>
        </Stack>
      </Stack>
      <Box mt={2}>
        <Typography variant="h6">General offer information</Typography>
        <CustomOfferRequiredFieldsLegend />
      </Box>
      <Grid mt={2} columns={6} spacing={2} container>
        <Grid size={{ xs: 3, md: 1 }}>
          <Controller
            name="item.type"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <FormControl fullWidth error={!!error}>
                <InputLabel required>Offer type</InputLabel>
                <Select
                  {...field}
                  label="Offer type"
                  disabled={isOfferTypeSelectDisabled}
                  onChange={handleOfferTypeChange}
                  data-testid="offer-type"
                >
                  {offerTypeSelectOptions.map((offerType) => (
                    <MenuItem key={offerType.value} value={offerType.value}>
                      {offerType.label}
                    </MenuItem>
                  ))}
                </Select>
                {error && <FormHelperText>{error.message}</FormHelperText>}
              </FormControl>
            )}
          />
        </Grid>

        <Grid size={{ xs: 3, md: 1 }}>
          <Controller
            name="supplier"
            control={control}
            render={({ field, fieldState }) => (
              <FormControl error={fieldState.invalid} fullWidth>
                <Autocomplete
                  {...field}
                  options={offerTypeSuppliers}
                  disabled={isSubmitting}
                  autoHighlight
                  renderInput={(params) => <TextField {...params} label="Select supplier" required />}
                  onChange={handleSupplierChange}
                  // For existing offers, set initial value based on vendor
                  value={isEditMode && itemVendor && offerTypeSuppliers.includes(itemVendor) ? itemVendor : field.value}
                />
                <FormHelperText>{fieldState.error?.message}</FormHelperText>
              </FormControl>
            )}
          />
        </Grid>

        <Grid hidden={supplier !== 'Other' || offerTypeSuppliers.includes(itemVendor)} size={{ xs: 3, md: 1 }}>
          <Controller
            name="item.vendor"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label="Supplier (Other)"
                required
                helperText={error?.message}
                error={!!error}
                disabled={isSubmitting}
                fullWidth
                data-testid="supplier"
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 3, md: 1 }}>
          <Controller
            name="item.vendor_reference_number"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label="Supplier booking reference"
                helperText={error?.message}
                error={!!error}
                disabled={isSubmitting}
                fullWidth
                data-testid="supplier-booking-reference"
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 3, md: 1 }}>
          <Controller
            name="sold_at"
            control={control}
            render={({ field: { ref: _, ...field }, fieldState: { error } }) => (
              <DateWidget
                {...field}
                label="Sales date"
                slotProps={{ textField: { helperText: error?.message, error: !!error, required: true } }}
                disabled={isSubmitting || hasOrders}
                fullWidth
                data-testid="sales-date"
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 3, md: 1 }}>
          <Controller
            name="payment_metadata.sales_agent_name"
            control={control}
            rules={{ required: 'Wholesale Agent is required' }}
            render={({ field, fieldState: { error } }) => (
              <WholesaleAgentAutocomplete
                value={field.value || ''}
                onChange={field.onChange}
                required
                error={!!error}
                helperText={error?.message}
                disabled={isSubmitting}
                options={WHOLESALE_AGENT_LIST}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 3, md: 1 }}>
          <Controller
            name="payment_metadata.sales_attribution"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label="Sales Attribution"
                required
                helperText={error?.message}
                error={!!error}
                disabled={isSubmitting || shouldLockLeLiveAttribution}
                select
                fullWidth
                slotProps={{ inputLabel: { shrink: !!field.value } }}
                data-testid="sales-attribution"
              >
                {getSalesAttributionList(field.value).map((attributionType) => (
                  <MenuItem
                    key={attributionType.value}
                    value={attributionType.value}
                    disabled={!attributionType.enabled}
                  >
                    {attributionType.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 2 }}>
          <Stack direction="row" spacing={1}>
            <Box sx={{ width: 120, flexShrink: 0 }}>
              <Controller
                name="customer_phone_prefix"
                control={control}
                render={({ field: { onChange, value, ...field } }) => (
                  <Autocomplete
                    {...field}
                    value={phonePrefixOptions.find((opt) => opt.value === value) ?? null}
                    onChange={(_, selected) => onChange(selected?.value ?? '')}
                    options={phonePrefixOptions}
                    getOptionLabel={(option) => `+${option.value}`}
                    renderOption={(props, option) => <li {...props}>{option.label}</li>}
                    filterOptions={(options, { inputValue }) => {
                      const input = inputValue.toLowerCase();
                      return options.filter((opt) => opt.label.toLowerCase().includes(input));
                    }}
                    slotProps={{ popper: { style: { minWidth: 280 }, placement: 'bottom-end' } }}
                    autoHighlight
                    disabled={isSubmitting}
                    renderInput={(params) => <TextField {...params} label="Prefix" />}
                    data-testid="customer-phone-prefix"
                  />
                )}
              />
            </Box>
            <Controller
              name="customer_phone_number"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label="Phone Number"
                  helperText={error?.message}
                  error={!!error}
                  disabled={isSubmitting}
                  fullWidth
                  inputProps={{ maxLength: 15 }}
                  data-testid="customer-phone-number"
                />
              )}
            />
          </Stack>
        </Grid>
      </Grid>
      <Grid mt={2} columns={6} spacing={2} container>
        <Grid size={isAdditionalMarginVisible ? 5 : 6}>
          <TextField
            {...register('item.name')}
            label="Offer name"
            required
            helperText={
              errors.item?.name
                ? errors.item.name.message
                : 'This is a customer-facing field and will be visible on customer portal and emails'
            }
            slotProps={{ inputLabel: { shrink: !!watch('item.name') } }}
            error={!!errors.item?.name}
            disabled={isSubmitting}
            fullWidth
            data-testid="offer-name"
          />
        </Grid>
        {isAdditionalMarginVisible && (
          <Grid size={1}>
            <TextField
              {...register('payment_metadata.additional_margin')}
              label="Additional margin"
              error={!!errors.payment_metadata?.additional_margin}
              helperText={errors.payment_metadata?.additional_margin?.message}
              disabled={isSubmitting}
              type="number"
              slotProps={{ htmlInput: { step: 0.01 } }}
              fullWidth
              data-testid="additional-margin"
            />
          </Grid>
        )}
      </Grid>
      <Grid mt={2} columns={4} spacing={2} container>
        <Grid size={{ xs: 3, md: 1 }}>
          <Controller
            name="reason"
            control={control}
            render={({ field: { ref: _, ...field }, fieldState: { error } }) => (
              <TextField
                {...field}
                label="Reason for custom offer"
                required
                helperText={error?.message}
                error={!!error}
                disabled={isSubmitting}
                select
                fullWidth
                data-testid="reason-for-custom-offer"
              >
                {REASONS.map((reason, index) => (
                  <MenuItem value={reason.value} key={index}>
                    {reason.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>
        {reason === 'other' && (
          <Grid size={2}>
            <Controller
              name="reasonOther"
              control={control}
              render={({ field: { ref: _, ...field }, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label="Reason for custom offer - Other"
                  required={isReasonOtherTextRequired(reason)}
                  helperText={error?.message}
                  error={!!error}
                  disabled={isSubmitting}
                  fullWidth
                  data-testid="reason-for-custom-offer-other"
                />
              )}
            />
          </Grid>
        )}
      </Grid>
      <Grid mt={2} columns={4} spacing={2} container>
        <Grid size={{ xs: 3, md: 0.5 }}>
          <Controller
            name="item.start_date"
            control={control}
            render={({ field: { ref: _, ...field }, fieldState: { error } }) => (
              <DateWidget
                {...field}
                label="Start date"
                slotProps={{ textField: { helperText: error?.message, error: !!error, required: true } }}
                disabled={isSubmitting}
                fullWidth
                data-testid="start-date"
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 3, md: 0.5 }}>
          <Controller
            name="item.end_date"
            control={control}
            render={({ field: { ref: _, ...field }, fieldState: { error } }) => (
              <DateWidget
                {...field}
                slotProps={{ textField: { helperText: error?.message, error: !!error, required: true } }}
                disabled={isSubmitting}
                label="End date"
                minDate={watch(`item.start_date`) ? newDate(watch(`item.start_date`)) : undefined}
                data-testid="end-date"
              />
            )}
          />
        </Grid>
      </Grid>
      <Grid mt={2} columns={4} spacing={2} container>
        <Grid size={4}>
          <RichTextEditor
            {...register('item.description')}
            control={control}
            label="Description"
            required
            helperText={
              errors.item?.description
                ? errors.item.description.message
                : 'This is a customer-facing field and will be visible on customer portal and emails'
            }
            error={!!errors.item?.description}
            disabled={isSubmitting}
            data-testid="description"
          />
        </Grid>
        <Grid size={4}>
          <RichTextEditor
            {...register('item.service_information')}
            control={control}
            label="Terms & Conditions"
            required
            helperText={
              errors.item?.service_information
                ? errors.item.service_information.message
                : 'This is a customer-facing field and will be visible on customer portal and emails'
            }
            error={!!errors.item?.service_information}
            disabled={isSubmitting}
            data-testid="terms-and-conditions"
          />
        </Grid>
      </Grid>
      <TravellersSection
        ref={travellersSectionRef}
        control={control}
        register={register}
        errors={errors}
        setValue={setValue}
        isSubmitting={isSubmitting}
        isBusinessTraveller={isBusinessTraveller}
        isEditMode={isEditMode}
        businessEmployees={[]}
        customerHasPaid={customerHasPaid}
        itemType="ics_store"
      />
      <Box mt={2}>
        <Typography variant="h6">Vendor Offer Prices - {costPriceCurrencyCode}</Typography>
      </Box>
      <Grid mt={2} container columns={6} spacing={2}>
        <Grid size={{ xs: 3, md: 1 }}>
          <TextField
            {...register('cost_price')}
            helperText={errors.cost_price?.message}
            error={!!errors.cost_price}
            disabled={isSubmitting || lockVendorPriceFieldsForPaidOrder}
            slotProps={{ inputLabel: { shrink: true }, htmlInput: { step: 0.01 } }}
            label="Cost price"
            required
            type="number"
            fullWidth
            data-testid="cost-price"
          />
        </Grid>
        <Grid size={{ xs: 3, md: 1 }}>
          <TextField
            {...register('payment_metadata.margin')}
            helperText={errors.payment_metadata?.margin?.message}
            error={!!errors.payment_metadata?.margin}
            label="Margin"
            disabled={isSubmitting || lockVendorPriceFieldsForPaidOrder}
            type="number"
            defaultValue={190}
            slotProps={{ inputLabel: { shrink: true }, htmlInput: { step: 0.01 } }}
            fullWidth
            data-testid="margin"
          />
        </Grid>
        <Grid size={{ xs: 12, md: 1 }}>
          <Controller
            name="cost_price_currency"
            control={control}
            render={({ field, fieldState }) => (
              <FormControl error={fieldState.invalid} fullWidth>
                <Autocomplete
                  {...field}
                  options={costPriceCurrencies}
                  autoHighlight
                  disableClearable
                  renderInput={(params) => <TextField {...params} label="Vendor Currency" required />}
                  onChange={(_, data) => {
                    handleCostPriceCurrencyChange(field.name, data);
                  }}
                  data-testid="vendor-cost-price-currency"
                />
                {fieldState.error?.message && <FormHelperText error>{fieldState.error.message}</FormHelperText>}
              </FormControl>
            )}
          />
        </Grid>
        <Grid container direction="row" alignItems="center" size={{ xs: 12, md: 3 }}>
          <Typography variant="subtitle2">
            AUD to {costPriceCurrencyCode} Fx Rate = {vendorExchangeRate}
          </Typography>
          <HtmlTooltip
            title={
              <RatesTable
                vendorCurrency={costPriceCurrencyCode}
                customerCurrency={priceCurrencyCode}
                customerFxRates={customerFxRates}
                contractFxRates={contractFxRates}
              />
            }
            placement="right"
          >
            <IconButton>
              <HelpIcon />
            </IconButton>
          </HtmlTooltip>
        </Grid>
      </Grid>
      <Grid mt={2} container columns={6} spacing={2}>
        <Grid size={{ xs: 6, md: 2 }}>
          <Box>
            <CustomOfferLoyaltyCalculator
              productType="ics_store"
              sellPrice={pricingState.sell_price}
              currency={priceCurrencyCode}
              margin={Number(margin) || 0}
              leServiceFee={0}
              userId={user?.id_member}
              salesAttribution={salesAttribution}
              brand={brand}
            />
          </Box>
        </Grid>
      </Grid>
      <Box mt={2}>
        <Typography variant="h6">Customer payments AUD (Payment made by customer)</Typography>
      </Box>
      <Grid mt={2} container columns={6} spacing={2}>
        <Grid size={{ xs: 3, md: 1 }}>
          <TextField
            {...register('payment_metadata.final_amount_customer')}
            helperText={errors.payment_metadata?.final_amount_customer?.message}
            error={!!errors.payment_metadata?.final_amount_customer}
            disabled
            label="Final amount (customer)"
            type="number"
            fullWidth
            required
            data-testid="final-amount-customer"
          />
        </Grid>
        <Grid size={{ xs: 3, md: 1 }}>
          <Controller
            name="due_date"
            control={control}
            render={({ field: { ref: _, ...field }, fieldState: { error } }) => (
              <DateWidget
                {...field}
                slotProps={{
                  textField: {
                    helperText: error?.message,
                    error: !!error,
                    required: true,
                    slotProps: { inputLabel: { shrink: true } },
                  },
                }}
                disabled={hasOrders || isSubmitting}
                label="Final Amount due date - customer"
                disablePast
                data-testid="final-amount-due-date"
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 1 }}>
          <Controller
            name="payment_metadata.block_customer_payment_notifications"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox {...field} checked={field.value} data-testid="dont-notify-customer-on-payment" />}
                label="Don't notify customer on payment"
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 2 }}>
          <Controller
            name="payment_metadata.merchant_fee_enabled"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={field.value ?? true}
                    onChange={(e) => field.onChange(e.target.checked)}
                    onBlur={field.onBlur}
                    name={field.name}
                    inputRef={field.ref}
                    disabled={isSubmitting}
                    data-testid="merchant-fee-enabled"
                  />
                }
                label={customOfferMerchantFeeFieldLabel}
              />
            )}
          />
        </Grid>
      </Grid>
      {isEditMode && (
        <Grid mt={2} container columns={6} spacing={2}>
          <CustomOfferInternalDiscountField control={control} errors={errors} isSubmitting={isSubmitting} isReadOnly />
        </Grid>
      )}
      <Grid mt={2} container columns={6} spacing={2}>
        <Grid size={6}>
          <TextField
            {...register('notes')}
            helperText={errors.notes?.message}
            error={!!errors.notes}
            disabled={isSubmitting}
            label="Internal notes"
            minRows={2}
            fullWidth
            multiline
            data-testid="internal-notes"
          />
        </Grid>
      </Grid>
      {order?.id_orders && (
        <OrderDetailNotes
          orderId={order?.id_orders}
          customer={user}
          refreshData={() => {
            setTimeout(() => window.location.reload(), 200);
          }}
          offerId={offer?.id}
        />
      )}
      {isEditMode && (
        <Box mt={2}>
          <FormGroup row>
            <FormControlLabel
              control={
                <Checkbox
                  checked={inclusionsDiscussed}
                  onChange={(e) => setInclusionsDiscussed(e.target.checked)}
                  disabled={hasOrders || isSubmitting}
                  data-testid="inclusions-discussed"
                />
              }
              label="Inclusions discussed"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={termsExplained}
                  onChange={(e) => setTermsExplained(e.target.checked)}
                  disabled={hasOrders || isSubmitting}
                  data-testid="terms-explained"
                />
              }
              label="Terms and Conditions explained"
            />
          </FormGroup>
        </Box>
      )}
      {!isCustomOfferPackage && (
        <Stack direction="row" spacing={2}>
          <FormGroup row>
            <Controller
              name="hiddenFromCustomer"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      {...field}
                      checked={field.value}
                      disabled={isSubmitting}
                      data-testid="hidden-from-customer"
                    />
                  }
                  label="Hide from customer portal"
                />
              )}
            />
          </FormGroup>
          <CustomOfferLinkedPackageOfferField
            control={control}
            currentOffer={offer}
            userId={user.id_member}
            isSubmitting={isSubmitting}
          />
        </Stack>
      )}
      {isCustomOfferPackage && <CustomOfferPackageComponentsSection currentOffer={offer} userId={user.id_member} />}
      <Stack mt={2} direction="row" justifyContent="space-between" spacing={2}>
        <Stack direction="row" spacing={2}>
          <Button
            type="submit"
            startIcon={<SaveIcon />}
            variant="contained"
            disabled={isActionsDisabled}
            data-testid="save-button"
          >
            Save
          </Button>

          {showSendButton && (
            <Button
              type="button"
              startIcon={<CheckIcon />}
              variant="contained"
              color="secondary"
              disabled={isSendDisabled}
              onClick={onSendPayment}
              data-testid="confirm-button"
            >
              Confirm and show in my escapes
            </Button>
          )}
        </Stack>
        {offer?.status === 'draft' && (
          <Stack direction="row" spacing={2}>
            <Button
              type="button"
              variant="contained"
              color="error"
              disabled={isActionsDisabled}
              onClick={openDeleteDialog}
              data-testid="delete-button"
            >
              Delete
            </Button>
          </Stack>
        )}
        <ConfirmDeleteDialog
          isOpen={deleteDialogOpen}
          description={offer?.name}
          handleClose={closeDeleteDialog}
          handleDelete={deleteOffer}
        />
      </Stack>
    </form>
  );
}
